import React from 'react';
import { Box, Typography } from '@mui/material';
import { fcColors } from '../../../../styles/theme';

export interface PageHeaderProps {
  /** Leading MUI icon component (rendered at 32px in the accent color). */
  icon: React.ComponentType<{ sx?: object }>;
  title: string;
  /** Muted line under the title; a string is wrapped in a body2 Typography, JSX is rendered as-is. */
  subtitle?: React.ReactNode;
  /** Optional right-aligned content (chip, selector, etc.). */
  action?: React.ReactNode;
}

/**
 * Shared page-header chrome for the Financial Clearance surfaces: a white bar with an accent
 * bottom border, a leading icon, a title/subtitle stack, and an optional right-aligned action.
 * Extracted from the (previously duplicated) headers on the dashboards, worklist, and assignment
 * management pages so the chrome stays consistent in one place.
 */
const PageHeader: React.FC<PageHeaderProps> = ({ icon: Icon, title, subtitle, action }) => (
  <Box
    sx={{
      backgroundColor: '#ffffff',
      borderBottom: `2px solid ${fcColors.primary}`,
      p: 2.5,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 2,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Icon sx={{ fontSize: 32, color: fcColors.primary }} />
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 600, color: fcColors.dark, mb: 0.5 }}>
          {title}
        </Typography>
        {typeof subtitle === 'string' ? (
          <Typography variant="body2" sx={{ color: fcColors.muted }}>
            {subtitle}
          </Typography>
        ) : (
          subtitle
        )}
      </Box>
    </Box>
    {action}
  </Box>
);

export default PageHeader;
